import { useEffect, useState } from "react";
import { X, FileSearch, ShieldCheck, CalendarClock, ClipboardList, CarFront, Wrench } from "lucide-react";
import { api, photoUrl, formatApiError } from "../api";
import { EVENT_META, fmtDate, fmtKm, fmtTry, scoreColor, belgeTarih, fmtDateShort } from "../lib/labels";
import { autoVehicleImage, hasOwnPhoto } from "../lib/vehicleImages";

const PAINT_LABEL = { orijinal: "Orijinal", lokal_boya: "Lokal Boya", boyali: "Boyalı", degisen: "Değişen", hasarli: "Hasarlı", islem_gormus: "İşlem Görmüş", belirtilmedi: "Belirtilmedi", diger: "Diğer" };

const checklistSummary = (obj) => {
  if (!obj || typeof obj !== "object") return null;
  const vals = Object.values(obj);
  if (!vals.length) return null;
  const statusOf = (x) => (typeof x === "string" ? x : x && x.status) || "kontrol_edilmedi";
  const checked = vals.filter((x) => statusOf(x) !== "kontrol_edilmedi").length;
  const issues = vals.filter((x) => ["bakim_gerekli", "arizali"].includes(statusOf(x))).length;
  if (!checked) return null;
  return { checked, issues, total: vals.length };
};

export default function VehicleDossierModal({ request, onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/teklif/talep/${request.id}/arac-dosyasi`)
      .then((r) => setData(r.data))
      .catch((e) => setError(formatApiError(e)));
  }, [request.id]);

  const v = data && data.vehicle;
  const own = v && hasOwnPhoto(v);
  const img = v ? (own ? photoUrl(v.photo || (v.photos && v.photos[0])) : autoVehicleImage(v.brand, v.model)) : "";
  const belgeler = v ? belgeTarih(v.belgeler) : {};
  const history = v ? [...(v.history || [])].sort((a, b) => (b.date || "").localeCompare(a.date || "")) : [];
  const paintEntries = v ? Object.entries(v.paint || {}) : [];
  const paintIssues = paintEntries.filter(([, s]) => s && s !== "orijinal" && s !== "belirtilmedi");

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div data-testid="vehicle-dossier-modal"
        className="w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-2xl bg-slate-900 border border-white/10 shadow-2xl p-6 sm:p-8"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <FileSearch className="w-5 h-5 text-cyan-300" /> Araç Dosyası (salt okunur)
          </h2>
          <button data-testid="dossier-close" onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        {error ? (
          <p data-testid="dossier-error" className="text-sm text-red-400">{error}</p>
        ) : !data ? (
          <div className="flex justify-center py-10"><div className="w-8 h-8 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" /></div>
        ) : (
          <div className="space-y-6">
            <div className="flex flex-col sm:flex-row gap-5">
              <img src={img} alt={`${v.brand} ${v.model}`} className="w-full sm:w-52 h-36 object-cover rounded-xl border border-white/10" />
              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-3">
                  <h3 className="text-2xl font-extrabold text-white">{v.brand} {v.model} <span className="text-slate-400 font-semibold text-lg">{v.year}</span></h3>
                  <span data-testid="dossier-score" className="text-sm font-bold px-3 py-1 rounded-full border"
                    style={{ color: scoreColor(v.score), borderColor: `${scoreColor(v.score)}55`, backgroundColor: `${scoreColor(v.score)}14` }}>
                    Skor: {v.score}
                  </span>
                </div>
                <p className="mt-1 font-mono-vin text-sm text-cyan-300 tracking-widest">{v.vin}</p>
                {data.expected_price != null && (
                  <p data-testid="dossier-expected-price" className="mt-2 text-base font-bold text-amber-300">
                    Sahibinin fiyat beklentisi: {fmtTry(data.expected_price)}
                  </p>
                )}
              </div>
            </div>

            <div data-testid="dossier-specs" className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
              {[["Plaka", v.plate], ["Kilometre", fmtKm(v.km)], ["Yakıt", v.fuel], ["Vites", v.transmission],
                ["Kasa Tipi", v.body_type], ["Araç Türü", v.vehicle_type || "Otomobil"], ["Renk", v.color], ["Paket", v.trim], ["Motor", v.engine]]
                .map(([lbl, val]) => (
                  <div key={lbl} className="rounded-lg border border-white/5 bg-slate-950/40 px-3 py-2">
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{lbl}</p>
                    <p className="mt-0.5 text-sm font-bold text-slate-100">{val || "—"}</p>
                  </div>
                ))}
            </div>

            <div>
              <h4 className="text-sm font-bold text-white uppercase tracking-wider flex items-center gap-2"><CalendarClock className="w-4 h-4 text-cyan-300" /> Belgeler</h4>
              <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                {[["kasko", "Kasko"], ["sigorta", "Trafik Sigortası"], ["muayene", "Muayene"]].map(([k, lbl]) => (
                  <div key={k} data-testid={`dossier-belge-${k}`} className="rounded-lg border border-white/5 bg-slate-950/40 px-3 py-2">
                    <p className="text-xs font-bold text-slate-200">{lbl}</p>
                    <p className="text-xs text-slate-400 mt-0.5">Bitiş: {fmtDateShort(belgeler[k]?.bitis)}</p>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h4 className="text-sm font-bold text-white uppercase tracking-wider flex items-center gap-2"><CarFront className="w-4 h-4 text-cyan-300" /> Kaporta / Boya</h4>
              {paintIssues.length === 0 ? (
                <p data-testid="dossier-paint-clean" className="mt-2 text-sm text-emerald-300 font-semibold">Boya / değişen kaydı yok (orijinal)</p>
              ) : (
                <div data-testid="dossier-paint-issues" className="mt-2 flex flex-wrap gap-2">
                  {paintIssues.map(([part, st]) => (
                    <span key={part} className="text-xs font-semibold px-2.5 py-1 rounded-full border border-amber-500/40 bg-amber-500/10 text-amber-300">
                      {part}: {PAINT_LABEL[st] || st}
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div>
              <h4 className="text-sm font-bold text-white uppercase tracking-wider flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-cyan-300" /> Kontrol Özetleri</h4>
              <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                {[["Mekanik", v.mechanics], ["Elektrik", v.electrics], ["Lastik / Jant", { ...(v.tires || {}), ...(v.rims || {}) }]].map(([lbl, obj]) => {
                  const s = checklistSummary(obj);
                  return (
                    <div key={lbl} className="rounded-lg border border-white/5 bg-slate-950/40 px-3 py-2">
                      <p className="text-xs font-bold text-slate-200">{lbl}</p>
                      <p className={`text-xs mt-0.5 font-semibold ${!s ? "text-slate-500" : s.issues ? "text-amber-300" : "text-emerald-300"}`}>
                        {!s ? "Kayıt yok" : s.issues ? `${s.checked} kalem işaretli, ${s.issues} sorunlu` : `${s.checked} kalem işaretli, sorun yok`}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>

            <div>
              <h4 className="text-sm font-bold text-white uppercase tracking-wider flex items-center gap-2"><ClipboardList className="w-4 h-4 text-cyan-300" /> Geçmiş Kayıtları ({history.length})</h4>
              {history.length === 0 ? (
                <p className="mt-2 text-sm text-slate-500">Kayıt yok.</p>
              ) : (
                <div data-testid="dossier-history" className="mt-2 space-y-2">
                  {history.map((r) => {
                    const meta = EVENT_META[r.type] || EVENT_META.tescil;
                    return (
                      <div key={r.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-white/5 bg-slate-950/40 px-3.5 py-2.5">
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-slate-100 flex items-center gap-2 flex-wrap">
                            <span className={`text-[10px] px-2 py-0.5 rounded-full border ${meta.ring} ${meta.color}`}>{meta.label}</span>
                            {r.title}
                          </p>
                          <p className="text-xs text-slate-500 mt-0.5">{fmtDate(r.date)}{r.km != null && ` • ${fmtKm(r.km)}`}{r.servis && ` • ${r.servis}`}</p>
                        </div>
                        {fmtTry(r.cost) && <span className="text-sm font-bold text-cyan-300">{fmtTry(r.cost)}</span>}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {v.photos && v.photos.length > 0 && (
              <div>
                <h4 className="text-sm font-bold text-white uppercase tracking-wider flex items-center gap-2"><Wrench className="w-4 h-4 text-cyan-300" /> Fotoğraflar</h4>
                <div className="mt-2 flex flex-wrap gap-2">
                  {v.photos.map((p) => (
                    <a key={p} href={photoUrl(p)} target="_blank" rel="noreferrer">
                      <img src={photoUrl(p)} alt="" className="w-20 h-14 object-cover rounded-lg border border-white/10 hover:border-cyan-400/50 transition-colors" />
                    </a>
                  ))}
                </div>
              </div>
            )}

            <p className="text-[11px] text-slate-500 border-t border-white/5 pt-3">
              Bu dosya salt okunur görünümdür; araç sahibinin kayıtları üzerinde değişiklik yapamazsınız.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
